import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useParams } from "wouter";
import { Breadcrumbs, EmptyState, Loader } from "@threadws/jute";
import type { Agent } from "../../api/generated";
import {
  agentsCreateVersionMutation,
  agentsGetOptions,
  agentsGetQueryKey,
  agentsListQueryKey,
  agentsVersionsQueryKey,
  usersListOptions,
} from "../../api/generated/@tanstack/react-query.gen";
import { AgentRuns } from "./AgentRuns";
import { PromptVersionForm, type PromptVersionValues } from "./PromptVersionForm";
import { VersionHistory } from "./VersionHistory";

/** One agent: edit its prompt and model, see every version and roll back, and follow its runs. */
export function AgentPage() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { data: agent, isPending, error } = useQuery(agentsGetOptions({ path: { id } }));
  const { data: users = [] } = useQuery(usersListOptions());
  const createVersion = useMutation(agentsCreateVersionMutation());
  const toList = () => navigate("/agents");

  if (isPending) return <Loader label="Loading agent" />;
  // A malformed id fails validation (400), so treat it like a missing agent.
  const missing = error?.statusCode === 400 || error?.statusCode === 404;
  if (error && (missing || !agent)) {
    return (
      <EmptyState
        title={missing ? "Agent not found" : "Couldn't load agent"}
        description={missing ? undefined : error.message}
        actionLabel="Back to agents"
        onAction={toList}
      />
    );
  }

  /** After a save or a rollback: show the agent's new active version and the longer history. */
  const refresh = async (updated?: Agent) => {
    if (updated) queryClient.setQueryData(agentsGetQueryKey({ path: { id } }), updated);
    await Promise.all([
      updated ? undefined : queryClient.invalidateQueries({ queryKey: agentsGetQueryKey({ path: { id } }) }),
      queryClient.invalidateQueries({ queryKey: agentsVersionsQueryKey({ path: { id } }) }),
      queryClient.invalidateQueries({ queryKey: agentsListQueryKey() }),
    ]);
  };
  const save = async (values: PromptVersionValues) => {
    await createVersion.mutateAsync({ path: { id }, body: { ...values, activate: true } });
    await refresh();
  };

  return (
    <section className="flex w-full max-w-3xl flex-col gap-8 px-4">
      <div className="flex flex-col gap-3">
        <Breadcrumbs items={[{ label: "Agents", onClick: toList }, { label: agent.name }]} />
        <h1 className="text-2xl font-medium">{agent.name}</h1>
        <p className="text-sm text-text-secondary">
          {agent.description} Running <span className="font-mono">v{agent.activeVersion.version}</span>.
        </p>
      </div>
      <PromptVersionForm active={agent.activeVersion} onSubmit={save} />
      <section className="flex flex-col gap-2">
        <h2 className="text-lg font-medium">Versions</h2>
        <VersionHistory agent={agent} users={users} onActivated={refresh} />
      </section>
      <section className="flex flex-col gap-2">
        <h2 className="text-lg font-medium">Recent runs</h2>
        <AgentRuns agentId={agent.id} />
      </section>
    </section>
  );
}
